import type { Player, MatchFrame } from "@/types";
import { trackEvent } from "@/lib/audit";
import { getMatchRoster, ensureRostersLoaded, type RosterPlayer } from "@/lib/sports/matchRosters";

// Field: x [-50,50], y [-32,32]. MEX defends left goal (x=-50), attacks right (x=50).

// Active roster bound at runtime via setActiveRoster(matchId).
// "MEX"/"ARG" remain internal team aliases for home/away.
let _homeRoster: RosterPlayer[] = [];
let _awayRoster: RosterPlayer[] = [];
let _activeMatchId = "";

export function setActiveRoster(matchId: string) {
  ensureRostersLoaded();
  if (matchId === _activeMatchId && _homeRoster.length) return;
  const roster = getMatchRoster(matchId);
  _homeRoster = roster.homePlayers;
  _awayRoster = roster.awayPlayers;
  _activeMatchId = matchId;
  void trackEvent("professional_sequence_bound_to_roster", {
    matchId, home: roster.homeTeam, away: roster.awayTeam,
  });
}

function homeName(i: number): string { return _homeRoster[i]?.name ?? `H#${i + 1}`; }
function awayName(i: number): string { return _awayRoster[i]?.name ?? `A#${i + 1}`; }
function homeNumber(i: number): number { return _homeRoster[i]?.number ?? i + 1; }
function awayNumber(i: number): number { return _awayRoster[i]?.number ?? i + 1; }

// Initialize default roster eagerly so module-level players have valid names.
setActiveRoster("mex-arg");

type Slot = { x: number; y: number; role: Player["role"] };
const MEX_FORMATION: Slot[] = [
  { x: -46, y: 0, role: "GK" },
  { x: -34, y: -18, role: "DEF" }, { x: -34, y: -6, role: "DEF" },
  { x: -34, y: 6, role: "DEF" }, { x: -34, y: 18, role: "DEF" },
  { x: -16, y: -14, role: "MID" }, { x: -16, y: 0, role: "MID" }, { x: -16, y: 14, role: "MID" },
  { x: 4, y: -18, role: "FWD" }, { x: 8, y: 0, role: "FWD" }, { x: 4, y: 18, role: "FWD" },
];
const ARG_FORMATION: Slot[] = [
  { x: 46, y: 0, role: "GK" },
  { x: 34, y: -18, role: "DEF" }, { x: 34, y: -6, role: "DEF" },
  { x: 34, y: 6, role: "DEF" }, { x: 34, y: 18, role: "DEF" },
  { x: 16, y: -14, role: "MID" }, { x: 16, y: 0, role: "MID" }, { x: 16, y: 14, role: "MID" },
  { x: -4, y: -18, role: "FWD" }, { x: -8, y: 0, role: "FWD" }, { x: -4, y: 18, role: "FWD" },
];

export const initialPlayers22: Player[] = [
  ...MEX_FORMATION.map((s, i) => ({
    id: i,
    team: "MEX" as const,
    x: s.x, y: s.y, number: homeNumber(i), name: homeName(i), role: s.role,
  })),
  ...ARG_FORMATION.map((s, i) => ({
    id: 11 + i,
    team: "ARG" as const,
    x: s.x, y: s.y, number: awayNumber(i), name: awayName(i), role: s.role,
  })),
];

// Smooth easing
const ease = (a: number, b: number, k: number) => a + (b - a) * k;

// Returns target position of a player at time t (seconds, 0..120)
function targetPos(p: Player, t: number, ball: { x: number; y: number }, attackingMex: boolean): { x: number; y: number; sprint: boolean } {
  const isMex = p.team === "MEX";
  const base = isMex ? MEX_FORMATION[p.id] : ARG_FORMATION[p.id - 11];
  if (base.role === "GK") {
    return { x: base.x, y: Math.max(-5, Math.min(5, ball.y * 0.25)), sprint: false };
  }
  const phase = p.id * 0.5;
  // Pressing forward when attacking
  const advance = isMex && attackingMex ? Math.min(22, Math.max(0, (t - 25) * 1.1)) : 0;
  const retreat = !isMex && attackingMex ? Math.min(12, Math.max(0, (t - 28) * 0.7)) : 0;
  // Sprint window 38-50 (counterattack) for MEX FWD/MID
  const inSprint = isMex && t > 38 && t < 52 && (base.role === "FWD" || base.role === "MID");
  const sprintBoost = inSprint ? (t - 38) * 0.8 : 0;
  const tx = base.x + advance + sprintBoost + Math.sin(t * 0.7 + phase) * 2.2 - retreat * (isMex ? 0 : 1);
  const ty = base.y + Math.cos(t * 0.5 + phase) * 2.6 + (inSprint && base.y < 0 ? -1.5 : 0);
  return { x: tx, y: ty, sprint: inSprint && t > 40 };
}

// Ball trajectory: 0-25 build-up, 25-38 recovery midfield, 38-52 fast transition,
// 52-58 through-ball, 58-62 shot/goal, 62-90 celebration & restart
function ballAt(t: number): { x: number; y: number; arcY: number } {
  let bx = 0, by = 0, arcY = 0;
  if (t < 25) {
    bx = -10 + Math.sin(t * 0.4) * 14;
    by = Math.cos(t * 0.3) * 10;
  } else if (t < 38) {
    const k = (t - 25) / 13;
    bx = ease(-12, -2, k) + Math.sin(t * 0.5) * 4;
    by = ease(8, -2, k) + Math.cos(t * 0.4) * 3;
  } else if (t < 52) {
    const k = (t - 38) / 14;
    bx = ease(-2, 26, k);
    by = ease(-2, -16, k) + Math.sin(t * 0.8) * 1.5; // wide left run
  } else if (t < 58) {
    const k = (t - 52) / 6;
    bx = ease(26, 36, k);
    by = ease(-16, -4, k); // cut into box
  } else if (t < 60) {
    const k = (t - 58) / 2;
    bx = ease(36, 49, k);
    by = ease(-4, 0, k);
    arcY = Math.sin(k * Math.PI) * 4;
  } else if (t < 64) {
    bx = 49; by = 0; arcY = 0;
  } else {
    const k = Math.min(1, (t - 64) / 8);
    bx = ease(49, 0, k);
    by = ease(0, 0, k);
  }
  return { x: bx, y: by, arcY };
}

// Persistent state for smoothing (per-frame call OK because we re-init on demand)
const lastState = new Map<number, { x: number; y: number; vx: number; vy: number }>();

export function resetSimulationState() {
  lastState.clear();
  _lastTouch = null;
  _lastTouchEmitT = -10;
}

// ============================================================================
// LAST TOUCH TRACKER — identifies the player nearest the ball each frame.
// Persists the most recent contact (within TOUCH_RADIUS) so other systems
// (commentary, timeline, VAR, predictions) can reliably attribute events.
// ============================================================================
export interface LastTouchInfo {
  id: number;
  name: string;
  team: "MEX" | "ARG";
  number: number;
  t: number;
}
let _lastTouch: LastTouchInfo | null = null;
let _lastTouchEmitT = -10;
const TOUCH_RADIUS = 2.4;
const TOUCH_RADIUS_SQ = TOUCH_RADIUS * TOUCH_RADIUS;

export function getLastTouch(): LastTouchInfo | null {
  return _lastTouch;
}

function updateLastTouch(t: number, players: Player[], ball: { x: number; y: number }) {
  let best: { p: Player; d2: number } | null = null;
  for (const p of players) {
    if (p.role === "GK") continue; // GK rarely "last touch" except saves; skip for clarity
    const dx = p.x - ball.x;
    const dy = p.y - ball.y;
    const d2 = dx * dx + dy * dy;
    if (d2 < TOUCH_RADIUS_SQ && (!best || d2 < best.d2)) best = { p, d2 };
  }
  if (!best) return;
  const { p } = best;
  if (_lastTouch?.id === p.id) return;
  _lastTouch = {
    id: p.id,
    name: p.name ?? `#${p.number ?? p.id}`,
    team: p.team,
    number: p.number ?? 0,
    t,
  };
  // Throttled audit emit so we don't flood the bus.
  if (t - _lastTouchEmitT >= 3) {
    _lastTouchEmitT = t;
    void trackEvent("last_touch_player_updated", {
      id: _lastTouch.id,
      name: _lastTouch.name,
      team: _lastTouch.team,
    });
  }
}

export function getProFrame(t: number, dt = 0.05, mode: "visual" | "snapshot" = "visual"): MatchFrame & {
  attackingMex: boolean;
  inDanger: boolean;
  isShot: boolean;
  isGoal: boolean;
  momentum: number;
  freeKick: boolean;
  lastTouch: LastTouchInfo | null;
} {
  const ball = ballAt(t);
  const attackingMex = t > 25;
  const isShot = t >= 58 && t < 60;
  const isGoal = t >= 60 && t < 64;
  const inDanger = ball.x > 26;
  const momentum = Math.max(0, Math.min(1, (t - 25) / 25));

  const players: Player[] = initialPlayers22.map((p) => {
    const target = targetPos(p, t, ball, attackingMex);
    const prev = lastState.get(p.id) ?? { x: p.x, y: p.y, vx: 0, vy: 0 };
    if (mode === "snapshot") {
      return {
        ...p,
        name: p.team === "MEX" ? homeName(p.id) : awayName(p.id - 11),
        number: p.team === "MEX" ? homeNumber(p.id) : awayNumber(p.id - 11),
        x: target.x,
        y: target.y,
        vx: 0,
        vy: 0,
        sprinting: target.sprint,
        fatigue: Math.min(1, Math.max(0, (t / 120) * 0.7 + (p.role === "FWD" ? 0.2 : 0.1))),
      };
    }
    // DYNAMIC PLAYER MOTION — camera is locked elsewhere, so the players
    // themselves must carry the sense of speed: visible acceleration,
    // sprint bursts, real direction changes. Inertia stays high enough
    // to avoid jitter, but caps are lifted so motion reads as athletic.
    const roleAccel = p.role === "GK" ? 4.2 : p.role === "DEF" ? 5.4 : p.role === "MID" ? 6.4 : 7.2;
    const accelK = target.sprint ? 14 : roleAccel;
    const dragK = target.sprint ? 0.9 : 0.84;
    const ax = (target.x - prev.x) * accelK * dt;
    const ay = (target.y - prev.y) * accelK * dt;
    let vx = (prev.vx + ax) * dragK;
    let vy = (prev.vy + ay) * dragK;
    // Cap speed (units per frame). Sprint reads as a real burst.
    const maxV = target.sprint ? 1.6 : p.role === "FWD" ? 0.95 : p.role === "MID" ? 0.85 : 0.75;
    const vMag = Math.hypot(vx, vy);
    if (vMag > maxV) { vx = (vx / vMag) * maxV; vy = (vy / vMag) * maxV; }
    const x = prev.x + vx;
    const y = prev.y + vy;
    lastState.set(p.id, { x, y, vx, vy });
    const fatigue = Math.min(1, Math.max(0, (t / 120) * 0.7 + (p.role === "FWD" ? 0.2 : 0.1)));
    return {
      ...p,
      name: p.team === "MEX" ? homeName(p.id) : awayName(p.id - 11),
      number: p.team === "MEX" ? homeNumber(p.id) : awayNumber(p.id - 11),
      x, y, vx, vy,
      sprinting: target.sprint && Math.hypot(vx, vy) > 0.5,
      fatigue,
    };
  });

  const scoreMex = t >= 60 ? 2 : 1;
  const scoreArg = 1;
  const minute = 78 + Math.floor(t / 30);

  // Update last-touch tracker AFTER positions resolve. Snapshot reads happen
  // during React render, so keep them side-effect free.
  if (mode !== "snapshot") updateLastTouch(t, players, { x: ball.x, y: ball.y });

  return {
    t,
    players,
    ball: { x: ball.x, y: ball.y },
    scoreMex,
    scoreArg,
    minute,
    attackingMex,
    inDanger,
    isShot,
    isGoal,
    momentum,
    freeKick: false,
    lastTouch: _lastTouch,
  };
}

export type ProFrame = ReturnType<typeof getProFrame>;

// expose ball arc height for radar
export function ballArc(t: number): number {
  return ballAt(t).arcY;
}